/**
 * Estado de Resultados - Finance 360
 * Income statement / Profit & Loss
 */

import { useState, useEffect, useRef } from 'react';
import {
  TrendingUp,
  TrendingDown,
  Calendar,
  Download,
  RefreshCw,
  ChevronRight,
  ChevronDown,
  DollarSign,
  MinusCircle,
  PlusCircle,
  FileText,
  FileSpreadsheet
} from 'lucide-react';
import toast from 'react-hot-toast';
import { contabilidadApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import { LoadingPage } from '../../components/LoadingSpinner';
import MonthRangePicker from '../../components/MonthRangePicker';

import useFiltroPeriodo from '../../hooks/useFiltroPeriodo';
import usePeriodosContables from '../../hooks/usePeriodosContables';
// Toolbar button component
function ToolbarButton({ icon: Icon, label, onClick, disabled, variant = 'default' }) {
  const baseClasses = "flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px]";
  const variants = {
    default: "border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400",
    primary: "border border-blue-400 text-blue-600 hover:bg-blue-50 hover:border-blue-500",
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <Icon className="h-5 w-5 mb-1" />
      <span className="whitespace-nowrap">{label}</span>
    </button>
  );
}

function ToolbarSeparator() {
  return <div className="w-px h-12 bg-gray-300 mx-1" />;
}

export default function EstadoResultados() {
  const { currentEmisor } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [estado, setEstado] = useState(null);

  // Filtro de período unificado (MonthRangePicker). Default = mes actual.
  // Los períodos contables (mensuales o de 4 semanas) también son filtro (S2-02).
  const periodos = usePeriodosContables();
  const [filtroFecha, setFiltroFecha] = useFiltroPeriodo('EstadoResultados', () => {
    const n = new Date();
    const y = n.getFullYear();
    const m = String(n.getMonth() + 1).padStart(2, '0');
    const last = new Date(y, n.getMonth() + 1, 0).getDate();
    return {
      modo: 'mes',
      periodo: `${y}-${m}`,
      desde: `${y}-${m}-01`,
      hasta: `${y}-${m}-${String(last).padStart(2, '0')}`
    };
  });
  // Aliases para no romper referencias internas (export, llamadas API)
  const fechaDesde = filtroFecha.desde;
  const fechaHasta = filtroFecha.hasta;

  const [expandedSections, setExpandedSections] = useState({
    ingresos: true,
    costos: true,
    gastos: true
  });
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [exporting, setExporting] = useState(false);
  const exportMenuRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target)) {
        setShowExportMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // ── Filtro por centro de costo (ítem 028) ───────────────────────────────
  // Es para lo que existe la fase 3: un grupo con varias fincas necesita leer
  // el resultado DE UNA, no solo el consolidado. Vacío = consolidado, que es
  // como se veía antes de esto.
  const [centrosCosto, setCentrosCosto] = useState([]);
  const [centroCostoId, setCentroCostoId] = useState('');

  useEffect(() => {
    if (!currentEmisor) return;
    contabilidadApi.getCentrosCosto()
      .then(r => setCentrosCosto(r.data?.data || []))
      // Si el catálogo no carga, el selector no aparece y la pantalla sigue
      // dando el consolidado: no se rompe por un filtro.
      .catch(() => setCentrosCosto([]));
  }, [currentEmisor]);

  useEffect(() => {
    if (currentEmisor) {
      loadEstado();
    }
  }, [currentEmisor, filtroFecha, centroCostoId]);

  const loadEstado = async () => {
    setLoading(true);
    try {
      const response = await contabilidadApi.getEstadoResultados({
        fechaInicio: fechaDesde,
        fechaFin: fechaHasta,
        ...(centroCostoId ? { centroCostoId } : {})
      });
      setEstado(response.data.data);
    } catch (error) {
      console.error('Error loading estado de resultados:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const handleExport = async (format) => {
    setShowExportMenu(false);
    setExporting(true);
    try {
      const response = await contabilidadApi.exportEstadoResultados({
        fechaInicio: fechaDesde,
        fechaFin: fechaHasta,
        format,
        // Lo exportado tiene que ser lo que se está viendo. Sin esto, el PDF
        // saldría con el consolidado bajo el mismo título y nadie lo notaría.
        ...(centroCostoId ? { centroCostoId } : {})
      });

      const blob = new Blob([response.data], {
        type: format === 'pdf' ? 'application/pdf' : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fileName = `Estado_Resultados_${fechaDesde}_${fechaHasta}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Estado de Resultados exportado a ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting:', error);
      toast.error('Error al exportar el reporte');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return <LoadingPage />;
  }

  if (!estado) {
    return (
      <div className="text-center py-12">
        <TrendingUp className="h-12 w-12 text-gray-300 mx-auto mb-3" />
        <p className="text-gray-500">No hay datos para mostrar</p>
      </div>
    );
  }

  const utilidadNeta = estado.utilidadNeta || 0;
  const isProfit = utilidadNeta >= 0;

  return (
    <div className="space-y-4">
      {/* Toolbar */}
      <div className="bg-white rounded-lg border p-3 flex items-center justify-center gap-2 flex-wrap">
        <ToolbarButton
          icon={RefreshCw}
          label="Generar"
          onClick={loadEstado}
          disabled={loading}
          variant="primary"
        />

        <ToolbarSeparator />

        <div className="relative" ref={exportMenuRef}>
          <ToolbarButton
            icon={Download}
            label="Exportar"
            onClick={() => setShowExportMenu(!showExportMenu)}
            disabled={exporting || !estado}
          />
          {showExportMenu && (
            <div className="absolute top-full left-0 mt-1 bg-white border rounded-lg shadow-lg z-10 py-1 min-w-[170px] w-max">
              <button
                onClick={() => handleExport('pdf')}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm whitespace-nowrap text-gray-700 hover:bg-gray-100"
              >
                <FileText className="h-4 w-4 text-red-500" />
                Exportar PDF
              </button>
              <button
                onClick={() => handleExport('excel')}
                className="w-full flex items-center gap-2 px-4 py-2 text-sm whitespace-nowrap text-gray-700 hover:bg-gray-100"
              >
                <FileSpreadsheet className="h-4 w-4 text-green-500" />
                Exportar Excel
              </button>
            </div>
          )}
        </div>

        <ToolbarSeparator />

        {/* Filtro de período unificado */}
        <MonthRangePicker value={filtroFecha} onChange={setFiltroFecha} periodos={periodos} />

        {/* Centro de costo (ítem 028). Solo aparece si la empresa tiene
            catálogo: a quien no lo usa no se le agrega un control vacío. */}
        {centrosCosto.length > 0 && (
          <>
            <ToolbarSeparator />
            <select
              value={centroCostoId}
              onChange={(e) => setCentroCostoId(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 min-w-[180px]"
              title="Centro de costo"
            >
              <option value="">Todos los centros de costo</option>
              {centrosCosto.map(c => (
                <option key={c.id} value={c.id}>{c.codigo} — {c.nombre}</option>
              ))}
            </select>
          </>
        )}
      </div>

      {/* Result Card */}
      <div className={`rounded-xl border-2 p-6 ${
        isProfit ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`h-14 w-14 rounded-full flex items-center justify-center ${
              isProfit ? 'bg-green-100' : 'bg-red-100'
            }`}>
              {isProfit ? (
                <TrendingUp className="h-7 w-7 text-green-600" />
              ) : (
                <TrendingDown className="h-7 w-7 text-red-600" />
              )}
            </div>
            <div>
              <p className="text-sm text-gray-600">Resultado del Período</p>
              <p className={`text-3xl font-bold ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(Math.abs(utilidadNeta))}
              </p>
              <p className={`text-sm ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
                {isProfit ? 'Utilidad Neta' : 'Pérdida Neta'}
              </p>
            </div>
          </div>

          <div className="text-right">
            <p className="text-sm text-gray-500">Período</p>
            <p className="font-medium text-gray-900">
              {new Date(fechaDesde + 'T00:00:00').toLocaleDateString('es-CR')} - {new Date(fechaHasta + 'T00:00:00').toLocaleDateString('es-CR')}
            </p>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 mb-2">
            <PlusCircle className="h-4 w-4 text-green-500" />
            <span className="text-xs text-gray-500">Ingresos</span>
          </div>
          <p className="text-xl font-bold text-green-600">{formatCurrency(estado.ingresos.total)}</p>
        </div>

        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 mb-2">
            <MinusCircle className="h-4 w-4 text-orange-500" />
            <span className="text-xs text-gray-500">Costos</span>
          </div>
          <p className="text-xl font-bold text-orange-600">{formatCurrency(estado.costos.total)}</p>
        </div>

        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 mb-2">
            <DollarSign className="h-4 w-4 text-blue-500" />
            <span className="text-xs text-gray-500">Utilidad Bruta</span>
          </div>
          <p className="text-xl font-bold text-blue-600">{formatCurrency(estado.utilidadBruta)}</p>
        </div>

        <div className="bg-white rounded-xl border p-4">
          <div className="flex items-center gap-2 mb-2">
            <MinusCircle className="h-4 w-4 text-red-500" />
            <span className="text-xs text-gray-500">Gastos</span>
          </div>
          <p className="text-xl font-bold text-red-600">{formatCurrency(estado.gastos.total)}</p>
        </div>
      </div>

      {/* Detail Sections */}
      <div className="space-y-4">
        {/* Ingresos */}
        <div className="bg-white rounded-xl border">
          <button
            onClick={() => toggleSection('ingresos')}
            className="w-full flex items-center justify-between p-4 border-b hover:bg-gray-50"
          >
            <div className="flex items-center gap-2">
              {expandedSections.ingresos ? (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronRight className="h-5 w-5 text-gray-400" />
              )}
              <h3 className="font-semibold text-gray-900">INGRESOS</h3>
            </div>
            <span className="font-bold text-green-600">{formatCurrency(estado.ingresos.total)}</span>
          </button>

          {expandedSections.ingresos && estado.ingresos.detalle.length > 0 && (
            <div className="divide-y">
              {estado.ingresos.detalle.map((cuenta) => (
                <div key={cuenta.codigo} className="flex items-center justify-between px-6 py-3 hover:bg-gray-50">
                  <div>
                    <span className="text-xs text-gray-400 font-mono mr-2">{cuenta.codigo}</span>
                    <span className="text-sm">{cuenta.nombre}</span>
                  </div>
                  <span className="font-medium text-gray-900">{formatCurrency(cuenta.monto)}</span>
                </div>
              ))}
              {/* NIIF 15: el ingreso se presenta neto de devoluciones y descuentos (NC): bruto − devoluciones = ventas netas */}
              {(estado.ingresos.devoluciones || []).length > 0 && (
                <>
                  <div className="flex items-center justify-between px-6 py-3 bg-gray-50">
                    <span className="text-sm font-medium text-gray-700">Ventas brutas</span>
                    <span className="font-medium text-gray-900">{formatCurrency(estado.ingresos.bruto)}</span>
                  </div>
                  {estado.ingresos.devoluciones.map((cuenta) => (
                    <div key={`dev-${cuenta.codigo}`} className="flex items-center justify-between px-6 py-3 hover:bg-gray-50">
                      <div>
                        <span className="text-xs text-gray-400 font-mono mr-2">{cuenta.codigo}</span>
                        <span className="text-sm">Menos: {cuenta.nombre}</span>
                      </div>
                      <span className="font-medium text-red-600">({formatCurrency(cuenta.monto)})</span>
                    </div>
                  ))}
                  <div className="flex items-center justify-between px-6 py-3 bg-gray-50">
                    <span className="text-sm font-semibold text-gray-900">Ventas netas</span>
                    <span className="font-semibold text-gray-900">{formatCurrency(estado.ingresos.total)}</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* Costos */}
        <div className="bg-white rounded-xl border">
          <button
            onClick={() => toggleSection('costos')}
            className="w-full flex items-center justify-between p-4 border-b hover:bg-gray-50"
          >
            <div className="flex items-center gap-2">
              {expandedSections.costos ? (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronRight className="h-5 w-5 text-gray-400" />
              )}
              <h3 className="font-semibold text-gray-900">COSTO DE VENTAS</h3>
            </div>
            <span className="font-bold text-orange-600">{formatCurrency(estado.costos.total)}</span>
          </button>

          {expandedSections.costos && estado.costos.detalle.length > 0 && (
            <div className="divide-y">
              {estado.costos.detalle.map((cuenta) => (
                <div key={cuenta.codigo} className="flex items-center justify-between px-6 py-3 hover:bg-gray-50">
                  <div>
                    <span className="text-xs text-gray-400 font-mono mr-2">{cuenta.codigo}</span>
                    <span className="text-sm">{cuenta.nombre}</span>
                  </div>
                  <span className="font-medium text-gray-900">{formatCurrency(cuenta.monto)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Utilidad Bruta */}
        <div className="bg-blue-50 rounded-xl border border-blue-200 p-4">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-blue-900">UTILIDAD BRUTA</span>
            <span className="text-xl font-bold text-blue-600">{formatCurrency(estado.utilidadBruta)}</span>
          </div>
        </div>

        {/* Gastos */}
        <div className="bg-white rounded-xl border">
          <button
            onClick={() => toggleSection('gastos')}
            className="w-full flex items-center justify-between p-4 border-b hover:bg-gray-50"
          >
            <div className="flex items-center gap-2">
              {expandedSections.gastos ? (
                <ChevronDown className="h-5 w-5 text-gray-400" />
              ) : (
                <ChevronRight className="h-5 w-5 text-gray-400" />
              )}
              <h3 className="font-semibold text-gray-900">GASTOS OPERATIVOS</h3>
            </div>
            <span className="font-bold text-red-600">{formatCurrency(estado.gastos.total)}</span>
          </button>

          {expandedSections.gastos && estado.gastos.detalle.length > 0 && (
            <div className="divide-y">
              {estado.gastos.detalle.map((cuenta) => (
                <div key={cuenta.codigo} className="flex items-center justify-between px-6 py-3 hover:bg-gray-50">
                  <div>
                    <span className="text-xs text-gray-400 font-mono mr-2">{cuenta.codigo}</span>
                    <span className="text-sm">{cuenta.nombre}</span>
                  </div>
                  <span className="font-medium text-gray-900">{formatCurrency(cuenta.monto)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Utilidad Operativa */}
        <div className="bg-purple-50 rounded-xl border border-purple-200 p-4">
          <div className="flex items-center justify-between">
            <span className="font-semibold text-purple-900">UTILIDAD OPERATIVA</span>
            <span className="text-xl font-bold text-purple-600">{formatCurrency(estado.utilidadOperativa)}</span>
          </div>
        </div>

        {/* No operacional — va DEBAJO de la utilidad operativa: no son ventas.
            El diferencial cambiario se muestra neto (NIC 1), emparejando la
            ganancia con la pérdida, para que se vea el resultado real del
            período en vez de la ganancia arriba y la pérdida entre los gastos. */}
        {estado.noOperacional && (estado.noOperacional.total !== 0
          || estado.noOperacional.otrosIngresos?.detalle?.length > 0
          || estado.noOperacional.diferencialCambiario?.detalle?.length > 0) && (
          <div className="bg-white rounded-xl border">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="font-semibold text-gray-900">OTROS INGRESOS Y GASTOS (NO OPERACIONALES)</h3>
              <span className={`font-bold ${estado.noOperacional.total >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(estado.noOperacional.total)}
              </span>
            </div>
            <div className="divide-y">
              {estado.noOperacional.diferencialCambiario?.detalle?.length > 0 && (
                <div className="px-6 py-3 hover:bg-gray-50">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">Diferencial cambiario, neto</span>
                    <span className={`font-medium ${estado.noOperacional.diferencialCambiario.neto >= 0 ? 'text-gray-900' : 'text-red-600'}`}>
                      {formatCurrency(estado.noOperacional.diferencialCambiario.neto)}
                    </span>
                  </div>
                  <div className="text-xs text-gray-400 mt-1">
                    Ganancia {formatCurrency(estado.noOperacional.diferencialCambiario.positivo)}
                    {' · '}
                    Pérdida {formatCurrency(estado.noOperacional.diferencialCambiario.negativo)}
                  </div>
                </div>
              )}
              {estado.noOperacional.otrosIngresos?.detalle?.map((cuenta) => (
                <div key={cuenta.codigo} className="flex items-center justify-between px-6 py-3 hover:bg-gray-50">
                  <div>
                    <span className="text-xs text-gray-400 font-mono mr-2">{cuenta.codigo}</span>
                    <span className="text-sm">{cuenta.nombre}</span>
                  </div>
                  <span className="font-medium text-gray-900">{formatCurrency(cuenta.monto)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Utilidad Neta */}
        <div className={`rounded-xl border-2 p-4 ${
          isProfit ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'
        }`}>
          <div className="flex items-center justify-between">
            <span className={`font-bold ${isProfit ? 'text-green-900' : 'text-red-900'}`}>
              {isProfit ? 'UTILIDAD NETA' : 'PÉRDIDA NETA'}
            </span>
            <span className={`text-2xl font-bold ${isProfit ? 'text-green-600' : 'text-red-600'}`}>
              {formatCurrency(Math.abs(utilidadNeta))}
            </span>
          </div>
        </div>
      </div>

      {/* Waterfall Visualization - Responsive */}
      <div className="bg-white rounded-xl border p-3 sm:p-6">
        <h3 className="font-semibold text-gray-900 mb-4 sm:mb-6 text-center text-sm sm:text-base">Flujo del Resultado</h3>
        {(() => {
          // Calcular el máximo para escalar las barras proporcionalmente
          const maxValue = Math.max(
            estado.ingresos.total || 0,
            estado.costos.total || 0,
            estado.gastos.total || 0,
            Math.abs(utilidadNeta) || 0,
            1
          );
          const maxHeight = 120; // px máximo de altura
          const calcHeight = (value) => Math.max((value / maxValue) * maxHeight, 8);

          return (
            <div className="flex items-end justify-center gap-2 sm:gap-4 md:gap-6 pb-2">
              {/* Ingresos */}
              <div className="flex flex-col items-center" style={{ width: 'clamp(50px, 12vw, 80px)' }}>
                <div
                  className="w-full bg-green-500 rounded-t"
                  style={{ height: `${calcHeight(estado.ingresos.total)}px` }}
                />
                <p className="text-[9px] sm:text-xs text-gray-500 mt-2 text-center">Ingresos</p>
                <p className="text-[8px] sm:text-xs font-medium text-center">{formatCurrency(estado.ingresos.total)}</p>
              </div>

              {/* Minus */}
              <span className="text-lg sm:text-2xl text-gray-400 self-center pb-10">−</span>

              {/* Costos */}
              <div className="flex flex-col items-center" style={{ width: 'clamp(50px, 12vw, 80px)' }}>
                <div
                  className="w-full bg-orange-500 rounded-t"
                  style={{ height: `${calcHeight(estado.costos.total)}px` }}
                />
                <p className="text-[9px] sm:text-xs text-gray-500 mt-2 text-center">Costos</p>
                <p className="text-[8px] sm:text-xs font-medium text-center">{formatCurrency(estado.costos.total)}</p>
              </div>

              {/* Minus */}
              <span className="text-lg sm:text-2xl text-gray-400 self-center pb-10">−</span>

              {/* Gastos */}
              <div className="flex flex-col items-center" style={{ width: 'clamp(50px, 12vw, 80px)' }}>
                <div
                  className="w-full bg-red-400 rounded-t"
                  style={{ height: `${calcHeight(estado.gastos.total)}px` }}
                />
                <p className="text-[9px] sm:text-xs text-gray-500 mt-2 text-center">Gastos</p>
                <p className="text-[8px] sm:text-xs font-medium text-center">{formatCurrency(estado.gastos.total)}</p>
              </div>

              {/* Equals */}
              <span className="text-lg sm:text-2xl text-gray-400 self-center pb-10">=</span>

              {/* Resultado */}
              <div className="flex flex-col items-center" style={{ width: 'clamp(50px, 12vw, 80px)' }}>
                <div
                  className={`w-full rounded-t ${isProfit ? 'bg-green-600' : 'bg-red-600'}`}
                  style={{ height: `${calcHeight(Math.abs(utilidadNeta))}px` }}
                />
                <p className="text-[9px] sm:text-xs text-gray-500 mt-2 text-center">{isProfit ? 'Utilidad' : 'Pérdida'}</p>
                <p className="text-[8px] sm:text-xs font-medium text-center">{formatCurrency(Math.abs(utilidadNeta))}</p>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
